import React from 'react';
import { Box } from '@mui/material';
import CanvasCardFrame, { CanvasCardDragSharedProps } from './CanvasCardFrame';
import type { AnchorLineSpec } from '../utils/canvasAnchorUtils';
import ArchitectureIcon from '@mui/icons-material/Architecture';

export type CanvasAnchorCardProps = CanvasCardDragSharedProps & {
	direction?: string | null;
	lineSpec?: AnchorLineSpec;
	repositoryTitle?: string | null;
};

function CanvasAnchorCardBase({
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	lineSpec,
}: CanvasAnchorCardProps) {
	const line = lineSpec ? (
		<Box
			aria-hidden
			sx={{
				position: 'absolute',
				left: '50%',
				top: '50%',
				pointerEvents: 'none',
				zIndex: -1,
				...(lineSpec.direction === 'vertical'
					? {
						height: lineSpec.max - lineSpec.min,
						borderLeft: (theme: any) => `${lineSpec.isActive ? 2 : 1}px dashed ${theme.palette.primary.main}`,
					}
					: {
						width: lineSpec.max - lineSpec.min,
						borderTop: (theme: any) => `${lineSpec.isActive ? 2 : 1}px dashed ${theme.palette.primary.main}`,
					}),
				opacity: lineSpec.isActive ? 0.9 : 0.45,
				animation: lineSpec.isActive ? 'anchorLinePulse 0.9s ease-in-out infinite' : 'none',
				'@keyframes anchorLinePulse': {
					'0%': { opacity: 0.55 },
					'50%': { opacity: 1 },
					'100%': { opacity: 0.55 },
				},
			}}
		/>
	) : undefined;

	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			collectionColor={collectionColor}
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={'Anchor'}
			zIndex={0}
			sx={{ minWidth: 0, '& > .MuiBox-root': { color: 'text.secondary', transition: 'color 0.2s ease-in-out' }, '&:hover > .MuiBox-root': { color: 'primary.main' } }}
			decorations={line}
		>
			<Box display="flex" flexDirection="row" alignItems="center" gap={0.5} justifyContent="center">
				
				<ArchitectureIcon fontSize='small' />
			</Box>
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every anchor card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export const CanvasAnchorCard = React.memo(CanvasAnchorCardBase);
export default CanvasAnchorCard;
